try {
            require('./index.css');
          } catch (e) {
            if (e.code !== 'MODULE_NOT_FOUND') {
              throw e; // Re-throw unexpected errors
            }
            console.warn('index.css not found, proceeding without it.');
          }
          

import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Button } from '@design-system/button';
import { isBrowser } from '@phx-husky/browser-helpers';
import { useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

const ModalHeader = ({ topElements, showCloseButton, closeAriaLabel, closeModal, closeIconSize, }) => (jsxs(Fragment, { children: [topElements && (jsx("div", Object.assign({ className: "cds-modal-window__top-elements" }, { children: topElements }))), showCloseButton && (jsx("div", Object.assign({ className: "cds-modal-window__close-button-container" }, { children: jsx(Button, Object.assign({ className: "cds-modal-window__close-button", "aria-label": closeAriaLabel, onClick: closeModal, variant: "subtle-icon", iconPrefix: { props: { icon: 'close' } }, size: closeIconSize, autoFocus: true }, { children: closeAriaLabel !== null && closeAriaLabel !== void 0 ? closeAriaLabel : '' })) })))] }));

const findFocusableElements = (modalRef) => modalRef.current.querySelectorAll('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]');
const handleTabKey = (e, modalRef) => {
    if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = findFocusableElements(modalRef);
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }
        else if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        }
    }
};

const ModalWrapper = ({ children, className, fullPage, showOverlay, disableOnClickClose, close, ariaLabel, }) => {
    const modalRef = useRef(null);
    return (jsxs("div", Object.assign({ className: classNames('cds-modal', className, {
            'cds-modal--full-page': fullPage,
        }) }, { children: [showOverlay && jsx("div", { className: "cds-modal__overlay" }), jsx("div", Object.assign({ className: "cds-modal__wrapper", "aria-modal": true, "aria-hidden": true, role: "dialog", onClick: !disableOnClickClose && close ? close : undefined, "data-testid": "modal-wrapper", onKeyDown: (e) => handleTabKey(e, modalRef), ref: modalRef, "aria-label": ariaLabel }, { children: jsx("div", Object.assign({ className: classNames('modal__container', 'cds-modal__container', {
                        'cds-modal__container--full-page': fullPage,
                    }), onClick: (e) => e.stopPropagation(), "aria-hidden": true }, { children: children })) }))] })));
};

const Modal = (_a) => {
    var { isShowing, topElements, children, close, closeModal, disableOnClickClose, className, ariaLabel, showOverlay = true, closeAriaLabel = 'close', closeIconSize = 'small', showCloseButton = true, containerElement, fullPage, showHeader = true } = _a, rest = __rest(_a, ["isShowing", "topElements", "children", "close", "closeModal", "disableOnClickClose", "className", "ariaLabel", "showOverlay", "closeAriaLabel", "closeIconSize", "showCloseButton", "containerElement", "fullPage", "showHeader"]);
    const handleEscape = useCallback((event) => {
        if (event.keyCode === 27 && close)
            close();
    }, [close]);
    const portalContainer = containerElement || (isBrowser() && (document === null || document === void 0 ? void 0 : document.body));
    useEffect(() => {
        if (isShowing && close) {
            document === null || document === void 0 ? void 0 : document.addEventListener('keydown', handleEscape, false);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document === null || document === void 0 ? void 0 : document.removeEventListener('keydown', handleEscape, false);
            document.body.style.overflow = 'auto';
        };
    }, [close, isShowing, handleEscape]);
    return isShowing && portalContainer
        ? ReactDOM.createPortal(jsxs(ModalWrapper, Object.assign({ className: className, fullPage: fullPage, showOverlay: showOverlay, disableOnClickClose: disableOnClickClose, close: close, ariaLabel: ariaLabel }, rest, { children: [showCloseButton && (jsx("div", Object.assign({ className: "cds-modal__close-button-container" }, { children: jsx(Button, Object.assign({ className: "cds-modal__close-button", "aria-label": closeAriaLabel, onClick: closeModal, variant: "subtle-icon", iconPrefix: { props: { icon: 'close' } }, size: closeIconSize, autoFocus: true }, { children: closeAriaLabel })) }))), showHeader && (jsx(ModalHeader, { topElements: topElements, closeAriaLabel: closeAriaLabel, closeModal: closeModal, closeIconSize: closeIconSize })), jsx("div", Object.assign({ className: "cds-modal__content" }, { children: children }))] })), portalContainer)
        : null;
};

export { Modal };
